import { Button } from '@ipfabric/ui-components/Button/Button';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { type Control, useController } from 'react-hook-form';
import { formatDisplayLabel } from '../../shared/formatDisplayLabel';
import type { RuleFormValues } from './ruleFormSchema';

type RuleLabelsEditorProps = {
  control: Control<RuleFormValues>;
  error?: string;
  onOpenPicker: () => void;
  setValue: (
    name: 'labels',
    value: RuleFormValues['labels'],
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
};

export const RuleLabelsEditor = ({
  control,
  error,
  onOpenPicker,
  setValue,
}: RuleLabelsEditorProps) => {
  const { field } = useController({ control, name: 'labels' });
  const labels = field.value ?? [];

  const removeLabel = (index: number) => {
    const next = labels.filter((_, i) => i !== index);
    setValue('labels', next, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <FormField
      description={
        <span className='inline-flex flex-wrap items-center gap-3xs'>
          Use
          <Icon glyph='fa-plus-circle' size='sm' />
          Add label button to assign label(s).
        </span>
      }
      error={error}
      label='Labels'
      link={
        <Button
          dataTestId='rule-form-add-label-link'
          icon='fa-plus-circle'
          iconPosition='start'
          onClick={onOpenPicker}
          type='button'
          variant='plain'
        >
          Add label
        </Button>
      }
    >
      {labels.length > 0 ? (
        <div className='flex flex-col gap-2xs'>
          {labels.map((label, index) => (
            <div
              className='flex w-full items-center gap-sm'
              key={formatDisplayLabel(label.name, label.value)}
            >
              <span className='min-w-0 flex-1 text-sm'>
                {formatDisplayLabel(label.name, label.value)}
              </span>
              <TableButton
                accent='negative'
                dataTestId={`rule-form-label-remove-${index}`}
                icon='fa-trash-can'
                onClick={() => removeLabel(index)}
                type='button'
                variant='plain'
              />
            </div>
          ))}
        </div>
      ) : null}
    </FormField>
  );
};
